<template>
  <div class="h3 mb-4">Сопоставление групп услуг</div>
  <p class="paragraph-s_h mb-10 text-secondary">Выберите соответствующие услуги и сопоставьте их</p>
  <e-form v-model="isValid" ref="formRef" class="d-flex flex-column row-gap-4" data-wide-content>
    <e-alert
      v-if="integrationError === ChannelManagerCustomError.ChannelManagerAdditionalMappingRequiredError"
      variant="error"
      class="mb-10"
    >
      <div class="paragraph-m_h font-600">
        В настройки подключения на стороне Менеджера Каналов были внесены изменения
      </div>
      <div class="paragraph-s_h text-secondary mt-1">Необходимо настроить дополнительные сопоставления</div>
    </e-alert>
    <e-data-table :headers="headers" :items="translationTable">
      <template v-slot:[`item.cid`]="{ item }">
        <div class="d-flex align-center">
          <e-select
            v-model="item.cid"
            placeholder="Выберите группу услуг"
            item-title="name[0].value"
            item-value="cid"
            :items="serviceGroups"
            :error-messages="duplicated(item.cid, item.externalObject.id)"
            :rules="[required()]"
            :disabled="serviceGroups.some((sg) => sg.cid === item.cid && !sg.canChange)"
            @update:model-value="saveMapping"
          >
            <template #prepend-item>
              <div class="pa-2">
                <e-btn prepend-icon-src="plus" variant="link" size="M" @click="showServiceGroupDialog(item)">
                  Добавить группу услуг
                </e-btn>
              </div>
            </template>
          </e-select>
          <e-btn
            :disabled="!item.cid"
            icon
            size="S"
            variant="ghost"
            class="ml-1 text-link"
            :loading="loadingServiceGroupCid === item.cid"
            @click="editServiceGroup(item.cid)"
          >
            <e-icon name="documentText" />
          </e-btn>
        </div>
      </template>
    </e-data-table>

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn 
        append-icon-src="arrowRight" 
        :loading="combinedLoading" 
        variant="primary" 
        @click="submit"
      > 
        Далее 
      </e-btn>
    </teleport>
  </e-form>
  <create-service-group-dialog
    v-if="integration?.master"
    v-model="isCreateDialogOpened"
    :tariff-schedule-cid="tariffScheduleCid"
    :hotel-set-cid="integration.master.hotelSetCid"
    :show-confirmation="isEdit"
    @success="onAddServiceGroup"
  />
  <service-group-info-dialog
    v-if="integration?.master"
    v-model="isEditDialogOpened"
    :tariff-schedule-cid="tariffScheduleCid"
    :hotel-set-cid="integration.master.hotelSetCid"
    :service-group-cid="editServiceGroupCid"
    @loaded="loadingServiceGroupCid = undefined"
    @success="updateServiceGroupData"
  />
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { computed, ref, toRef } from 'vue';
import type { AddServiceGroupPayload, ServiceGroup } from '@/parts/tariffs/store/serviceGroups';
import type { TranslationTableItem } from '@/parts/tariffs/types/tariffs';
import { EAlert, EBtn, EDataTable, EForm, EIcon, ESelect } from '@/core/components/ui';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { required } from '@/core/utils/validation';
import { ChannelManagerCode } from '@/graphql/generated';
import CreateServiceGroupDialog from '@/parts/tariffs/components/ServiceGroups/CreateServiceGroupDialog.vue';
import ServiceGroupInfoDialog from '@/parts/tariffs/components/ServiceGroups/ServiceGroupInfoDialog.vue';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';
import { ChannelManagerCustomError } from '@/parts/tariffs/types/travel-line';
import { useRoute } from 'vue-router';

defineProps<{
  isEdit?: boolean;
}>();

const { integration, saveServiceGroupsMapping, updateServiceGroups, integrationError, channelManagerCode } =
  useTravelLineStore();

const $route = useRoute();
const isMounted = useMounted();

const tariffScheduleCid = toRef(() => $route.query.t_id ? String($route.query.t_id) : undefined);

const serviceGroups = toRef(
  () =>
    integration.value?.master?.steps.serviceGroupsMapping!.serviceGroups.map((x) => ({
      ...x,
      disabled: !x.canChange,
    })) || [],
);
const translationTable = toRef(() => integration.value?.master?.steps.serviceGroupsMapping?.translationTable || []);

const duplicated = (cid: string | null | undefined, externalId: string) => {
  if (!cid) {
    return '';
  }
  return translationTable.value.some((item) => item.cid === cid && item.externalObject.id !== externalId)
    ? 'Группа услуг уже сопоставлена'
    : '';
};

let translationItemAfterAddServiceGroup: TranslationTableItem | null = null;

const onAddServiceGroup = (data: AddServiceGroupPayload) => {
  if (!integration.value?.master) {
    return;
  }

  if (data.addedServiceGroup) {
    integration.value.master.steps.serviceGroupsMapping!.serviceGroups.push({
      cid: data.addedServiceGroup.serviceGroupCid,
      name: data.addedServiceGroup.name,
      canChange: true,
    });
  }

  if (translationItemAfterAddServiceGroup) {
    translationItemAfterAddServiceGroup.cid = data.addedServiceGroup?.serviceGroupCid;
    saveMapping();
  }
};

const updateServiceGroupData = (data: ServiceGroup) => {
  if (!integration.value?.master || !data) {
    return;
  }

  const existingSG = serviceGroups.value.find((s) => s.cid === data.cid);

  if (existingSG) {
    existingSG.name = data.name;
  }
};

const isCreateDialogOpened = ref(false);
const isEditDialogOpened = ref(false);
const editServiceGroupCid = ref<string>('');
const loadingServiceGroupCid = ref<string>();

const showServiceGroupDialog = (item: TranslationTableItem) => {
  translationItemAfterAddServiceGroup = item;
  isCreateDialogOpened.value = true;
};

const editServiceGroup = (cid: string | null | undefined) => {
  if (!cid) {
    return;
  }

  editServiceGroupCid.value = cid;
  loadingServiceGroupCid.value = cid;
  isEditDialogOpened.value = true;
};

const getMapping = () =>
  translationTable.value
    .filter((t) => t.cid && serviceGroups.value.some((s) => s.cid === t.cid && s.canChange))
    .map((t) => ({
      externalID: t.externalObject.id,
      cid: t.cid!,
    }));

const isValid = ref(false);
const formRef = ref();

const isSavingMapping = ref(false);

const saveMapping = async () => {
  isSavingMapping.value = true;
  
  try {
    await saveServiceGroupsMapping(getMapping());
  } finally {
    isSavingMapping.value = false;
  }
};

const isLoading = ref(false);

const combinedLoading = computed(() => isSavingMapping.value || isLoading.value);

const submit = async () => {
  formRef.value.validate();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updateServiceGroups(getMapping());

  isLoading.value = false;

  if (!result?.errors) {
    integrationError.value = undefined;
  }
};

const headers = [
  {
    key: 'externalObject.name',
    title: channelManagerCode.value === ChannelManagerCode.TravelLine ? 'Группа услуг TL' : 'Группа услуг Exely',
    sortable: false,
  },
  {
    key: 'cid',
    title: 'Группа услуг Экстранет',
    sortable: false,
  },
];
</script>

<style scoped lang="scss"></style>
